// Crash-proof JSON <-> localStorage primitives.
//
// The bug class these close: `JSON.stringify(undefined)` returns the VALUE
// undefined rather than a string, and `localStorage.setItem(key, undefined)`
// coerces that to the literal string "undefined". Reading it back with a bare
// `JSON.parse` throws — and a throw during render or an effect takes the whole
// React tree down. One missing field in one API response was enough to leave a
// permanent white screen that survived reloads.
//
// Every persisted-JSON read/write in the app should go through here so that
// storage can never hold a value that breaks the next render.

/**
 * Parse JSON without ever throwing.
 * @returns the parsed value, or `fallback` if the input is absent/blank/invalid.
 */
const MISSING = Symbol("safeJson.missing");

export const safeJsonParse = (raw, fallback = null) => {
  if (raw === null || raw === undefined) return fallback;
  if (typeof raw !== "string") return raw; // already deserialized
  const trimmed = raw.trim();
  // "undefined" and "null" are the two values that get into storage by
  // coercion rather than by an intentional write.
  if (!trimmed || trimmed === "undefined") return fallback;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
};

/**
 * Serialize for storage, refusing values that would round-trip into a poisoned
 * string.
 * @returns a JSON string, or "" when the value is not storable.
 */
export const safeJsonStringify = (value) => {
  if (value === null || value === undefined) return "";
  try {
    const serialized = JSON.stringify(value);
    return typeof serialized === "string" && serialized !== "undefined"
      ? serialized
      : "";
  } catch {
    // Circular reference, BigInt, a throwing toJSON() — all better handled here
    // than as an uncaught exception in whatever component triggered the write.
    return "";
  }
};

/**
 * Read a JSON value out of localStorage. A corrupt entry is REMOVED on the way
 * out, so a browser that already holds a poisoned value heals itself instead of
 * failing on every subsequent load.
 */
export const readJson = (key, fallback = null) => {
  let raw = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    // Storage can throw on ACCESS, not just on write (private mode, "block site
    // data"). Treat that as "nothing stored".
    return fallback;
  }
  if (raw === null) return fallback;
  // A unique sentinel, NOT `undefined`: passing undefined would trigger
  // safeJsonParse's own default parameter and silently become `null`, so a
  // poisoned entry would be reported as a legitimate null and never removed.
  const parsed = safeJsonParse(raw, MISSING);
  if (parsed === MISSING) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* nothing more we can do */
    }
    return fallback;
  }
  return parsed;
};

/**
 * Write a JSON value to localStorage. An unusable value REMOVES the key rather
 * than storing something that breaks the next read.
 * @returns true if a value was stored.
 */
export const writeJson = (key, value) => {
  const serialized = safeJsonStringify(value);
  try {
    if (!serialized) {
      localStorage.removeItem(key);
      return false;
    }
    localStorage.setItem(key, serialized);
    return true;
  } catch (e) {
    // Quota exceeded / storage disabled. Never let persistence failure become a
    // crash — the caller's flow is more important than the cache write.
    console.warn(`[safeJson] could not persist "${key}":`, e?.message);
    return false;
  }
};
